import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePapaParse } from "react-papaparse";
import LoadingSpinner from "../components/LoadingSpinner";
import { checkExhausted, isArrayOf, isString } from "../core/utils";
import { parseFrontMatter } from "../markdown/frontMatter";
import {
  DatasetConfig,
  Filter,
  applyFilters,
  isConfig,
  sanitizeConfig,
} from "./config";
import { dataToArticles, initFilters, parseRawTable } from "./parse";
import { Article, ColumnPair, DataSourceContextValue, Source } from "./types";
import useEmbedParameters from "../hooks/useEmbedParameters";
import { useEffectOnce } from "../hooks/useEffectOnce";

export const DataSourceContext = createContext<DataSourceContextValue | null>(
  null
);

interface DataSourceProviderProps {
  children: ReactNode;
}

export function DataSourceProvider(
  props: DataSourceProviderProps
): ReactElement {
  const { children } = props;

  const { mapboxToken, configUrl, csvUrl, spreadsheet, additionalCSS } =
    useEmbedParameters();

  if (mapboxToken == null) {
    throw new Error("Mapbox token not found");
  }
  if (configUrl == null) {
    throw new Error("Dataset config not found");
  }

  useEffectOnce(() => {
    if (additionalCSS != null) {
      const pageLinkEl =
        document.querySelector<HTMLLinkElement>("#additional-css");
      const linkEl = pageLinkEl ?? document.createElement("link");
      linkEl.id = "additional-css";
      linkEl.rel = "stylesheet";
      linkEl.href = additionalCSS;
      if (pageLinkEl == null) {
        document.head.appendChild(linkEl);
      }
    }
  });

  const source = useMemo((): Source | null => {
    if (csvUrl != null) {
      return { type: "CSV", url: csvUrl };
    } else if (spreadsheet != null) {
      return { type: "GoogleSheets", spreadsheet };
    } else {
      return null;
    }
  }, [csvUrl, spreadsheet]);

  if (source == null) {
    throw new Error("Embed source not configured correctly");
  }

  const [csvHeader, setCsvHeader] = useState<Array<string>>([]);
  const [allArticles, setAllArticles] = useState<Array<Article> | null>(null);
  const [focused, setFocused] = useState<Article | null>(null);
  const [tempArticle, setTempArticle] = useState<Article | null>(null);
  const [filters, setFilters] = useState<Array<Filter>>([]);
  const [mapHighlight, setMapHighlight] = useState<Array<ColumnPair> | null>(
    null
  );

  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (error != null) {
      throw error;
    }
  }, [error]);

  const [config, setConfig] = useState<DatasetConfig<string> | null>(null);
  const [markdownTemplate, setMarkdownTemplate] = useState<string | null>(null);

  const { readString } = usePapaParse();

  const [rawTextLines, setRawTextLines] = useState<Array<string> | null>(null);
  const [rawSourceData, setRawSourceData] = useState<string[][] | null>(null);

  useEffect(() => {
    if (rawSourceData != null && config != null && rawTextLines != null) {
      const articleData = parseRawTable(rawSourceData);
      const [articles, articlesError] = dataToArticles(
        articleData,
        config,
        rawTextLines
      );
      const [filters, filtersError] = initFilters(articleData, config.filters);

      if (articles != null && filters != null) {
        setAllArticles(articles);
        setFilters(filters);
      } else {
        setError(articlesError ?? filtersError);
      }
    }
  }, [config, rawSourceData, rawTextLines]);

  const fetchData = useCallback(
    (url: string, networkErrReason: string) =>
      fetch(url)
        .then((resp) =>
          200 <= resp.status && resp.status < 300
            ? resp.text()
            : Promise.reject(new Error(networkErrReason))
        )
        .then((text) => {
          const textLines = text.split("\n");
          setCsvHeader(textLines.slice(0, 2));
          setRawTextLines(textLines.slice(2));
          readString(text, {
            worker: true,
            download: false,
            complete: ({ errors, data }) => {
              if (errors.length > 0) {
                setError(
                  new Error(
                    `Could not read CSV: ${errors
                      .map(({ message }) => message)
                      .join(", ")}`
                  )
                );
              } else if (isArrayOf(isArrayOf(isString))(data)) {
                setRawSourceData(data);
              }
            },
          });
        })
        .catch(setError),
    [readString]
  );

  const fetchConfig = useCallback(
    (url: string) =>
      fetch(url)
        .then((resp) => resp.text())
        .then((contents) => {
          const { frontMatter, restOfContents } = parseFrontMatter(contents);
          if (isConfig(frontMatter)) {
            setConfig(sanitizeConfig(frontMatter));
            setMarkdownTemplate(restOfContents);
          } else {
            setError(new Error("Dataset config not found."));
          }
        })
        .catch(setError),
    []
  );

  useEffect(() => {
    switch (source.type) {
      case "CSV":
        Promise.all([
          fetchData(
            `${source.url}`,
            "Invalid CSV url. Make sure that the CSV exists at the given URL"
          ),
          fetchConfig(configUrl),
        ]);
        break;
      case "GoogleSheets":
        Promise.all([
          fetchData(
            `https://docs.google.com/spreadsheets/d/${source.spreadsheet}/export?format=csv`,
            "Invalid google sheets IDs. Please make sure you copied each ID correctly and the sheet is public"
          ),
          fetchConfig(configUrl),
        ]);
        break;
      default:
        return checkExhausted(source);
    }
  }, [configUrl, fetchConfig, fetchData, source]);

  const value = useMemo(
    (): DataSourceContextValue | null =>
      allArticles != null && config != null && markdownTemplate != null
        ? {
            csvHeader,
            config,
            mapboxToken,
            markdownTemplate,
            articles: applyFilters(filters, allArticles),
            allArticles,
            focused,
            tempArticle,
            filters,
            mapHighlight,
            setFocused,
            setTempArticle,
            setFilters,
            setMapHighlight,
          }
        : null,
    [
      allArticles,
      config,
      csvHeader,
      filters,
      focused,
      mapHighlight,
      mapboxToken,
      markdownTemplate,
      tempArticle,
    ]
  );

  return value != null ? (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  ) : (
    <LoadingSpinner text="Loading Data" />
  );
}
